import { api } from "./api";
import { authService } from "./auth.service";
import type { ProductIngredient, Allergen } from "@/types";

export type ProductType = "STARTER" | "MAIN_COURSE" | "DESSERT" | "DRINK" | "SIDE";
export type DishCategory = "FISH" | "GRILLED_MEAT" | "VEGETARIAN" | "TRADITIONAL" | "EXOTIC" | "STEW" | "OTHER";
export type Weekday = "MONDAY" | "TUESDAY" | "WEDNESDAY" | "THURSDAY" | "FRIDAY" | "SATURDAY" | "SUNDAY";

/** Entrée pour la composition d'un produit (création / mise à jour) */
export interface ProductIngredientInput {
  ingredientId: string;
  quantity?: number;
  unit?: string;
  isPrimary?: boolean;
  displayOrder?: number;
}

export interface Product {
  id: string;
  name: string;
  description?: string | null;
  categoryId: string;
  category?: {
    id: string;
    name: string;
  };
  price: number | string; // Prisma Decimal retourne un string
  type: ProductType;
  dishCategory?: DishCategory | null;
  isVegetarian: boolean;
  isRecurrent?: boolean;
  recurrentDays?: Weekday[];
  isPremium?: boolean;
  isSpicy?: boolean;
  isSwitch?: boolean;
  isAvailable: boolean;
  displayOrder: number;
  imageUrl?: string | null;
  createdAt: string;
  updatedAt: string;
  /** Composition du produit (présent quand chargé via getById ou getAll avec include) */
  ingredients?: ProductIngredient[];
}

export interface CreateProductData {
  name: string;
  description?: string;
  categoryId: string;
  price: number;
  type: ProductType;
  dishCategory?: DishCategory;
  isVegetarian?: boolean;
  isRecurrent?: boolean;
  recurrentDays?: Weekday[];
  isPremium?: boolean;
  isSpicy?: boolean;
  isSwitch?: boolean;
  isAvailable?: boolean;
  displayOrder?: number;
  ingredients?: ProductIngredientInput[];
}

export interface UpdateProductData extends Partial<CreateProductData> {}

export interface ProductFilter {
  search?: string;
  categoryId?: string;
  type?: ProductType;
  dishCategory?: DishCategory;
  availability?: "all" | "available" | "unavailable";
  isVegetarian?: boolean;
  isRecurrent?: boolean;
  isPremium?: boolean;
}

/**
 * Service pour la gestion des produits
 */
export const productService = {
  /**
   * Récupère tous les produits
   */
  async getAll(filters?: ProductFilter): Promise<Product[]> {
    const params: Record<string, string | boolean> = {};
    if (filters?.categoryId) params.categoryId = filters.categoryId;
    if (filters?.type) params.type = filters.type;
    if (filters?.dishCategory) params.dishCategory = filters.dishCategory;
    if (filters?.availability) params.availability = filters.availability;
    if (filters?.isVegetarian === true) params.isVegetarian = "true";
    if (filters?.isVegetarian === false) params.isVegetarian = "false";
    if (filters?.isRecurrent === true) params.isRecurrent = "true";
    if (filters?.isRecurrent === false) params.isRecurrent = "false";
    if (filters?.isPremium === true) params.isPremium = "true";
    if (filters?.isPremium === false) params.isPremium = "false";
    if (filters?.search?.trim()) params.search = filters.search.trim();

    const response = await api.get<{ success: boolean; data?: Product[] }>(
      "/products",
      { params },
    );
    return response.data?.data ?? [];
  },

  /**
   * Récupère un produit par son ID (avec composition ingrédients)
   */
  async getById(id: string): Promise<Product> {
    const response = await api.get<{ success: boolean; data: Product }>(
      `/products/${id}`,
    );
    return response.data?.data as Product;
  },

  /**
   * Crée un nouveau produit
   */
  async create(data: CreateProductData): Promise<Product> {
    const response = await api.post<{ success: boolean; data: Product }>(
      "/products",
      data,
    );
    return response.data.data;
  },

  /**
   * Met à jour un produit
   */
  async update(id: string, data: UpdateProductData): Promise<Product> {
    const response = await api.put<{ success: boolean; data: Product }>(
      `/products/${id}`,
      data,
    );
    return response.data.data;
  },

  /**
   * Supprime un produit
   */
  async delete(id: string): Promise<void> {
    await api.delete(`/products/${id}`);
  },

  /**
   * Duplique un produit (même données et ingrédients, nom avec " (copie)")
   */
  async duplicate(id: string): Promise<Product> {
    if (!authService.getToken()) {
      throw new Error("Session expirée. Veuillez vous reconnecter.");
    }
    if (!id) {
      throw new Error("ID du produit manquant.");
    }
    // URL dédiée hors de /products pour éviter 404 (proxy / router)
    const response = await api.post<{ success: boolean; data: Product }>(
      `/duplicate-product/${encodeURIComponent(id)}`,
    );
    return response.data.data;
  },

  /**
   * Récupère les allergènes d'un produit (déduits des ingrédients)
   */
  async getAllergens(productId: string): Promise<Allergen[]> {
    const response = await api.get<{ success: boolean; data: Allergen[] }>(
      `/products/${productId}/allergens`,
    );
    return response.data?.data ?? [];
  },
};
